import {
  V1_CAREER_DOCUMENTS_AVAILABILITY_PATH,
  V1_CAREER_DOCUMENTS_PATH,
} from '@/constant/routers/path';
import { http } from '@/services/http.service';
import request from '@/services/http.service';
import type { AxiosResponse } from 'axios';

import type {
  CareerDocumentAvailabilityResponse,
  CareerDocumentProfileActionResponse,
  CreateCareerDocumentRequest,
  CreateCareerDocumentResult,
} from './career-documents.d';

function getResponseHeader(headers: AxiosResponse['headers'], name: string): string | undefined {
  if (!headers) return undefined;
  if (typeof headers.get === 'function') {
    const value = headers.get(name);
    return value == null ? undefined : String(value);
  }
  const normalized = name.toLowerCase();
  const record = headers as Record<string, string | string[] | undefined>;
  const value = record[normalized] ?? record[name];
  if (value == null) return undefined;
  return Array.isArray(value) ? value[0] : String(value);
}

function parseContentDispositionFileName(contentDisposition: string | undefined): string {
  if (!contentDisposition) return 'career_document.docx';
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }
  const asciiMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return asciiMatch?.[1] ?? 'career_document.docx';
}

async function parseBlobErrorMessage(blob: Blob): Promise<string | null> {
  try {
    const text = await blob.text();
    const json = JSON.parse(text) as { message?: string; error?: { message?: string } };
    return json.message ?? json.error?.message ?? null;
  } catch {
    return null;
  }
}

export const getCareerDocumentAvailability = (): Promise<CareerDocumentAvailabilityResponse> => {
  return request<CareerDocumentAvailabilityResponse>(V1_CAREER_DOCUMENTS_AVAILABILITY_PATH, {
    method: 'GET',
  });
};

export const createCareerDocument = async (
  payload: CreateCareerDocumentRequest
): Promise<CreateCareerDocumentResult> => {
  const response: AxiosResponse<Blob> = await http.request({
    url: V1_CAREER_DOCUMENTS_PATH,
    method: 'POST',
    data: payload,
    responseType: 'blob',
    validateStatus: () => true,
  });

  if (response.status < 200 || response.status >= 300) {
    const message = await parseBlobErrorMessage(response.data);
    throw new Error(message ?? 'Wordファイルの出力に失敗しました。');
  }

  const contentType = getResponseHeader(response.headers, 'content-type') ?? '';
  if (contentType.includes('application/json')) {
    const message = await parseBlobErrorMessage(response.data);
    throw new Error(message ?? 'Wordファイルの出力に失敗しました。');
  }

  const careerDocumentId = getResponseHeader(response.headers, 'x-career-document-id');
  if (!careerDocumentId) {
    throw new Error('Wordファイルの出力に失敗しました。（IDが取得できませんでした）');
  }

  const summaryStatus =
    getResponseHeader(response.headers, 'x-career-document-summary-status') ??
    getResponseHeader(response.headers, 'x-summary-status');

  return {
    blob: response.data,
    careerDocumentId,
    summaryStatus: summaryStatus ?? null,
    fileName: parseContentDispositionFileName(
      getResponseHeader(response.headers, 'content-disposition')
    ),
  };
};

export const uploadCareerDocumentToProfile = (
  careerDocumentId: string
): Promise<CareerDocumentProfileActionResponse> => {
  return request<CareerDocumentProfileActionResponse>(
    `${V1_CAREER_DOCUMENTS_PATH}/${careerDocumentId}/upload_to_profile`,
    { method: 'POST' }
  );
};

export const applyCareerDocumentSummaryToProfile = (
  careerDocumentId: string
): Promise<CareerDocumentProfileActionResponse> => {
  return request<CareerDocumentProfileActionResponse>(
    `${V1_CAREER_DOCUMENTS_PATH}/${careerDocumentId}/apply_to_profile`,
    { method: 'POST' }
  );
};
